import {useCallback, useMemo} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {requiredValidator} from '../../utils'
import {NumberInput} from '../formComponents'

export type OnEditMaterialDemandFn = (
  v: number,
  materialId: number,
  params: {onError: () => void; onSuccess: () => void}
) => void

export function MaterialDemandTableFieldEdit({
  value,
  onEdit,
  materialId
}: {
  value: number
  onEdit?: OnEditMaterialDemandFn
  materialId: number
}) {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {control, handleSubmit, reset} = useForm<{value: number}>({
    defaultValues: {value}
  })

  const resetToDefault = useCallback(() => {
    reset({value})
  }, [reset, value])

  const onSubmit = useMemo(
    () =>
      handleSubmit(({value: newValue}) => {
        onEdit?.(newValue, materialId, {
          onError: resetToDefault,
          onSuccess: () => reset({value: newValue})
        })
      }),
    [handleSubmit, onEdit, materialId, resetToDefault, reset]
  )

  return (
    <Controller
      control={control}
      name="value"
      rules={{...requiredValidator(t)}}
      render={({field: {ref, value, onChange}, fieldState: {error}}) => (
        <NumberInput
          inputRef={ref}
          min={0}
          value={value}
          onChange={onChange}
          onBlur={onSubmit}
          size="small"
          error={error?.message}
          dataTestId="value_input"
        />
      )}
    />
  )
}
